import { forwardRef, isValidElement, ReactElement, ReactNode, type ComponentProps } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn, AiGradientIcon, Icon, Flex, Tag, type SvgIcon, Text } from '@bridebook/aura';
import {
  InfoCircle,
  ChevronRight,
  Close,
  TriangleWarning,
  Sparkle,
  CheckCircleSolid,
} from '@bridebook/aura/src/icons';
import {
  PolymorphicComponentPropsWithRef,
  PolymorphicRef,
} from '@bridebook/aura/src/lib/type-helpers';

type SupportMessageVariantProps = VariantProps<typeof variants>;

type BaseSupportMessageProps = {
  /**
   * Icon displayed on the left
   * - if true, shows default icon based on theme
   * - if false, no icon is shown
   * - if SvgIcon, custom icon is shown
   * - if ReactElement, rendered as-is in the icon slot
   */
  icon?: boolean | SvgIcon | ReactElement;
  /**
   * Title text displayed prominently
   */
  title?: string;
  /**
   * Description text displayed below title
   */
  description?: string;
  /**
   * Tag text displayed above the title
   */
  tag?: string;
  /**
   * Props for the tag component
   */
  tagProps?: Omit<ComponentProps<typeof Tag>, 'theme'>;
} & VariantProps<typeof variants>;

// Discriminated union to prevent onClick with onClose/actions
type SupportMessageProps =
  | (BaseSupportMessageProps & {
      /**
       * Click handler for interactive messages
       * Cannot be used with onClose or actions (will be ignored)
       */
      onClick?: () => void;
      onClose?: never;
      actions?: never;
    })
  | (BaseSupportMessageProps & {
      onClick?: never;
      /**
       * Callback when close icon is clicked
       * When provided, displays a close icon on the right side
       */
      onClose?: () => void;
      /**
       * Action buttons displayed below description on mobile, right side on desktop
       */
      actions?: ReactNode;
    });

const getDefaultIcon = (theme: SupportMessageVariantProps['theme']): SvgIcon => {
  switch (theme) {
    case 'warning':
      return TriangleWarning;
    case 'critical':
      return TriangleWarning;
    case 'success':
      return CheckCircleSolid;
    case 'info':
    default:
      return InfoCircle;
  }
};

const getTagTheme = (
  theme: SupportMessageVariantProps['theme'],
): Parameters<typeof Tag>[0]['theme'] => {
  switch (theme) {
    case 'warning':
      return 'peachRose60';
    case 'critical':
      return 'blushTangerine60';
    case 'success':
      return 'teal80';
    case 'info':
    default:
      return 'indigoCrush20';
  }
};

const variants = cva('flex w-full items-start gap-2 rounded-lg p-4 text-left text-space', {
  variants: {
    theme: {
      info: 'border border-indigo-crush-60 bg-indigo-crush-04',
      warning: 'border border-peach-rose-120 bg-peach-rose-20',
      critical: 'border border-blush-tangerine bg-pink-joy',
      success: 'border border-teal-120 bg-teal-20',
    },
  },
});

const defaultComponent = 'div';

interface PolymorphicSupportMessage {
  <C extends React.ElementType = typeof defaultComponent>(
    props: PolymorphicComponentPropsWithRef<C, SupportMessageProps>,
  ): React.ReactNode;
}

const SupportMessageComponent = <C extends React.ElementType = typeof defaultComponent>(
  {
    as,
    theme = 'info',
    icon = true,
    title,
    description,
    onClose,
    className,
    children,
    tag,
    tagProps,
    actions,
    ...props
  }: PolymorphicComponentPropsWithRef<C, SupportMessageProps>,
  ref: PolymorphicRef<C>,
) => {
  const iconNode = (() => {
    if (!icon) return null;
    if (isValidElement(icon)) return icon;
    const IconSvg = icon === true ? getDefaultIcon(theme) : (icon as SvgIcon);
    return <Icon icon={IconSvg} size={16} />;
  })();

  // Make element interactive only if no onClose or actions are provided, to avoid conflicting interactions
  // and for a valid HTML without nested buttons
  const isInteractive =
    !onClose &&
    !actions &&
    (as === 'button' || as === 'a' || 'onClick' in props || 'href' in props);
  const showChevronIcon = isInteractive;
  const showCloseIcon = Boolean(onClose);
  const Component = isInteractive ? as || 'button' : defaultComponent;

  return (
    <Component
      ref={ref}
      className={cn(
        '@container',
        variants({ theme }),
        isInteractive &&
          'cursor-pointer focus-visible:ring-[3px] focus-visible:ring-focus-ring focus-visible:ring-offset-2 focus-visible:outline-none',
        className,
      )}
      {...props}>
      {iconNode && !tag && (
        <div className="flex min-h-[1lh] shrink-0 items-center typo-sm">{iconNode}</div>
      )}

      <Flex className="flex-1 flex-col gap-3 @2xl:flex-row @2xl:items-center @2xl:gap-4">
        <Flex className="flex-1 flex-col gap-2">
          {tag && (
            <Tag theme={getTagTheme(theme)} {...tagProps}>
              {tag}
            </Tag>
          )}
          {(title || description) && (
            <Flex className="flex-1 flex-col gap-1">
              {title && <Text className="typo-sm-semibold">{title}</Text>}
              {description && <Text className="typo-sm">{description}</Text>}
            </Flex>
          )}
          {children && <div className="typo-sm">{children}</div>}
        </Flex>
        {actions && <div>{actions}</div>}
      </Flex>

      {showCloseIcon && (
        <button
          type="button"
          aria-label="Close"
          className="cursor-pointer self-start rounded focus-visible:ring-[3px] focus-visible:ring-focus-ring focus-visible:ring-offset-2 focus-visible:outline-none"
          onClick={(e) => {
            e.stopPropagation();
            onClose?.();
          }}>
          <Icon icon={Close} size={16} className="shrink-0" />
        </button>
      )}

      {showChevronIcon && (
        <div className="self-center">
          <Icon icon={ChevronRight} size={16} className="shrink-0" />
        </div>
      )}
    </Component>
  );
};

export const SupportMessage: PolymorphicSupportMessage = forwardRef(SupportMessageComponent);

const gradientBorderVariants = cva('rounded-[11px] p-[3px]', {
  variants: {
    variant: {
      default: '',
      ai: 'bg-ai-gradient',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

/**
 * Special variant of SupportMessage with a gradient border.
 * Separate component required for CSS hacks for gradient border.
 * The `ai` variant uses the AI gradient for both the border and the icon fill.
 */
type SupportMessageGradientBorderProps = Omit<SupportMessageProps, 'theme'> &
  VariantProps<typeof gradientBorderVariants>;

interface PolymorphicSupportMessageGradientBorder {
  <C extends React.ElementType = typeof defaultComponent>(
    props: PolymorphicComponentPropsWithRef<C, SupportMessageGradientBorderProps>,
  ): React.ReactNode;
}

const SupportMessageGradientBorderComponent = <
  C extends React.ElementType = typeof defaultComponent,
>(
  {
    as,
    className,
    variant = 'default',
    icon = Sparkle,
    ...props
  }: PolymorphicComponentPropsWithRef<C, SupportMessageGradientBorderProps>,
  ref: PolymorphicRef<C>,
) => {
  // The ai variant paints the icon glyph with the same gradient as the border
  const resolvedIcon =
    variant === 'ai' && icon && !isValidElement(icon) ? (
      <AiGradientIcon className="flex">
        <Icon icon={icon === true ? Sparkle : (icon as SvgIcon)} size={16} />
      </AiGradientIcon>
    ) : (
      icon
    );

  return (
    <div
      className={cn(gradientBorderVariants({ variant }), className)}
      style={
        variant === 'default'
          ? {
              background:
                'linear-gradient(145deg, var(--color-indigo-crush-80) 0%, var(--color-teal-80) 71%)',
            }
          : undefined
      }>
      <SupportMessage
        as={as}
        {...(props as any)}
        icon={resolvedIcon}
        ref={ref}
        theme="info"
        className="border-none"
      />
    </div>
  );
};

export const SupportMessageGradientBorder: PolymorphicSupportMessageGradientBorder = forwardRef(
  SupportMessageGradientBorderComponent,
);
